import { Settings2, RotateCcw, Eye, EyeOff, Rows, Rows3, Pin, PinOff } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * 149-as: Standard Table View Settings popover.
 * Terhubung ke `useTableLayoutV2` hook — user kontrol:
 *   - Hide / unhide setiap kolom via checkbox
 *   - Density: Compact / Normal
 *   - Reset ke default
 *
 * Props:
 *  - layout: return value dari useTableLayoutV2
 *  - align: 'start' | 'center' | 'end' (default 'end')
 */
export default function TableViewSettings({ layout, align = "end" }) {
  const { allColumns, isHidden, toggleHidden, density, setDensity, resetLayout, hasCustomLayout, getPin, setPin } = layout;
  const visibleCount = allColumns.filter((c) => !isHidden(c.key)).length;
  const cyclePin = (key) => {
    const cur = getPin?.(key);
    if (!cur) setPin?.(key, "left");
    else if (cur === "left") setPin?.(key, "right");
    else setPin?.(key, null);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="table-view-settings-btn"
          className={`h-9 px-3 border ${hasCustomLayout ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-slate-300 text-slate-700"} bg-white hover:bg-slate-50 flex items-center gap-1.5 uppercase text-[11px] tracking-wider font-semibold`}
          title="Pengaturan tampilan tabel"
        >
          <Settings2 size={13} /> Tampilan{hasCustomLayout ? " ✱" : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-72 rounded-none p-0" data-testid="table-view-settings-popover">
        <div className="border-b border-slate-200 px-3 py-2 bg-slate-50 flex items-center justify-between">
          <span className="overline text-slate-600 text-[10px] font-semibold">Tampilan Tabel</span>
          {hasCustomLayout && (
            <button
              type="button"
              onClick={resetLayout}
              data-testid="table-reset-layout"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-rose-700 hover:bg-rose-50 border border-rose-300"
            ><RotateCcw size={10} /> Reset</button>
          )}
        </div>

        {/* Density */}
        <div className="px-3 py-2 border-b border-slate-200">
          <div className="overline text-slate-500 text-[10px] mb-1.5">Kepadatan</div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setDensity("normal")}
              data-testid="density-normal"
              className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold border ${density === "normal" ? "border-[#0B1D3A] bg-[#0B1D3A] text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
            ><Rows size={11} /> Normal</button>
            <button
              type="button"
              onClick={() => setDensity("compact")}
              data-testid="density-compact"
              className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold border ${density === "compact" ? "border-[#0B1D3A] bg-[#0B1D3A] text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
            ><Rows3 size={11} /> Compact</button>
          </div>
        </div>

        {/* Column visibility */}
        <div className="px-3 py-2">
          <div className="overline text-slate-500 text-[10px] mb-1.5 flex items-center justify-between">
            <span>Kolom Tampil</span>
            <span className="normal-case tabular-nums text-slate-400">{visibleCount}/{allColumns.length}</span>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-0.5" data-testid="table-view-settings-cols">
            {allColumns.map((c) => {
              const hidden = isHidden(c.key);
              const pin = getPin?.(c.key);
              return (
                <div key={c.key} className={`w-full flex items-center gap-1 px-2 py-1 text-xs transition-colors ${hidden ? "text-slate-400" : "text-[#0B1D3A]"}`}>
                  <button
                    type="button"
                    onClick={() => toggleHidden(c.key)}
                    data-testid={`col-toggle-${c.key}`}
                    className={`flex-1 flex items-center gap-2 px-1 py-0.5 text-left hover:bg-amber-50 ${hidden ? "hover:bg-slate-50" : ""}`}
                  >
                    {hidden ? <EyeOff size={12} /> : <Eye size={12} className="text-[#FF5A1F]" />}
                    <span className={hidden ? "line-through" : "font-medium truncate"}>{c.label}</span>
                  </button>
                  {/* 149-at: pin toggle — cycle none → left → right → none. Icon Pin/PinOff + label L/R */}
                  <button
                    type="button"
                    onClick={() => cyclePin(c.key)}
                    data-testid={`col-pin-${c.key}`}
                    title={pin ? `Pinned ${pin.toUpperCase()} — klik untuk cycle` : "Pin kolom (kiri/kanan)"}
                    className={`h-6 w-8 flex items-center justify-center border text-[9px] font-bold uppercase tracking-wider transition-colors ${pin === "left" ? "border-[#FF5A1F] bg-orange-50 text-[#FF5A1F]" : pin === "right" ? "border-[#0B1D3A] bg-slate-50 text-[#0B1D3A]" : "border-slate-200 text-slate-400 hover:border-slate-400"}`}
                  >
                    {pin ? <>{pin === "left" ? "◀L" : "R▶"}</> : <PinOff size={10} />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
